import React, { useEffect, useMemo, useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useLocale, useT } from '../../i18n'
import type { Lot } from '../../types/lot'
import type { FeedLog } from '../../types/production'
import type { WizardFarm } from '../../utils/farmWizard'
import { lotApi } from '../../utils/lotApi'
import { feedLogApi } from '../../utils/feedLogApi'
import { farmTimeZone, farmToday } from '../../utils/farmTime'
import { loadLotStockAndWeightAt } from '../../utils/lotFishMove'
import {
  bandIndexForWeight,
  daysBetween,
  fastingDaysBefore,
  fastingRecord,
  readPlannedTransfer,
  readTransferProtocol,
  readTransferRation,
  withPlannedTransfer
} from '../../utils/transferProtocol'

// Findings #210: "Programar traslado" — the office sets the day a lot will be
// moved and the band of the Protocolo de traslado, so the phone shows "Ayuno
// hoy" on the fasting days. Also shows the last move's fasting record (feed
// recorded on each fasting day) and its reduced ration.

export default function LotTransferPlanRow({ lot, farm, canEdit, onChanged }: {
  lot: Lot
  farm: WizardFarm | undefined
  canEdit: boolean
  onChanged: () => void
}) {
  const t = useT()
  const locale = useLocale()
  const timeZone = farmTimeZone(lot.farmId)
  const today = farmToday(timeZone)
  const bands = useMemo(() => readTransferProtocol(farm), [farm])
  const planned = readPlannedTransfer(lot)
  const ration = readTransferRation(lot)

  const [feedLogs, setFeedLogs] = useState<FeedLog[]>([])
  const fastingDays = useMemo(() => {
    const fromPlan = planned ? fastingDaysBefore(planned.date, planned.band.fastDays) : []
    return [...(ration?.fastingDays ?? []), ...fromPlan].filter((day, index, all) => day <= today && all.indexOf(day) === index)
  }, [planned, ration, today])
  useEffect(() => {
    if (fastingDays.length === 0) return
    let cancelled = false
    void feedLogApi.list({ farmId: lot.farmId, lotId: lot.id }, { preferLocal: true })
      .then((logs) => { if (!cancelled) setFeedLogs(logs) })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [lot.farmId, lot.id, fastingDays.length])

  const [editing, setEditing] = useState(false)
  const [date, setDate] = useState('')
  const [bandChoice, setBandChoice] = useState('')
  const [proposedIndex, setProposedIndex] = useState(-1)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const startEditing = () => {
    setDate(planned?.date ?? '')
    setBandChoice(planned ? String(Math.max(0, bands.findIndex((band) => JSON.stringify(band) === JSON.stringify(planned.band)))) : '')
    setError(null)
    setEditing(true)
    if (lot.productionUnitId) {
      const unitName = ((farm?.productionUnits ?? []) as Array<{ id: string; name?: string }>).find((unit) => unit.id === lot.productionUnitId)?.name
      void loadLotStockAndWeightAt({ farmId: lot.farmId, farmName: farm?.name, productionUnitId: lot.productionUnitId, productionUnitName: unitName, lotId: lot.id, asOf: new Date().toISOString() })
        .then((result) => setProposedIndex(bandIndexForWeight(bands, result.weightG)))
        .catch(() => setProposedIndex(-1))
    }
  }

  const save = async (clear = false) => {
    const index = bandChoice === '' ? proposedIndex : Number(bandChoice)
    const band = index >= 0 ? bands[index] : undefined
    if (!clear && (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date < today || !band)) {
      setError(t('lots.transferPlan.invalid'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const next = withPlannedTransfer(lot, clear || !band ? null : { date, band, plannedAt: new Date().toISOString() })
      await lotApi.update(lot.id, { metadata: next.metadata })
      setEditing(false)
      onChanged()
    } catch (saveError) {
      console.warn('[lots] failed to save the planned move', saveError)
      setError(t('lots.transferPlan.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  if (bands.length === 0 && !planned && !ration) return null

  const fmt = (value: number, digits = 1) => value.toLocaleString(locale, { maximumFractionDigits: digits })
  const formatDay = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' })
  const records = fastingRecord(fastingDays, feedLogs, lot.id, timeZone)
  const rationDay = ration ? daysBetween(ration.movedOn, today) + 1 : 0
  const label: React.CSSProperties = { fontSize: 12, color: '#475569' }

  return (
    <div data-testid="lot-transfer-plan" style={{ padding: 12, borderRadius: 10, border: '1px solid #fcd34d', backgroundColor: '#fffbeb', display: 'grid', gap: 6, fontSize: 13 }}>
      <strong style={{ color: '#78350f' }}>{t('lots.transferPlan.title')}</strong>

      {planned && !editing ? (
        <span data-testid="lot-transfer-plan-planned">
          {t('lots.transferPlan.planned', {
            date: formatDay(planned.date),
            fasting: planned.band.fastDays > 0
              ? fastingDaysBefore(planned.date, planned.band.fastDays).map(formatDay).join(', ')
              : t('lots.transferPlan.noFasting'),
            days: planned.band.reducedDays,
            delta: fmt(planned.band.deltaC)
          })}
        </span>
      ) : null}

      {ration ? (
        <span data-testid="lot-transfer-plan-ration">
          {t('lots.transferPlan.lastMove', { date: formatDay(ration.movedOn) })}{' '}
          {ration.band.reducedDays > 0
            ? t(ration.reducedKgPerDay === null ? 'lots.transferPlan.rationNoKg' : 'lots.transferPlan.ration', {
              kg: fmt(ration.reducedKgPerDay ?? 0, 3),
              days: ration.band.reducedDays,
              delta: fmt(ration.band.deltaC)
            })
            : null}
          {ration.extrapolated ? ` ${t('lots.transferPlan.extrapolated')}` : ''}
          {rationDay >= 1 && rationDay <= ration.band.reducedDays ? ` ${t('lots.transferPlan.today', { day: rationDay, of: ration.band.reducedDays })}` : ''}
        </span>
      ) : null}

      {records.length > 0 ? (
        <div data-testid="lot-transfer-plan-fasting" style={{ display: 'grid', gap: 2 }}>
          <span style={label}>{t('lots.transferPlan.fastingRecord')}</span>
          {records.map((record) => (
            <span key={record.day} style={{ color: record.fedKg > 0 ? '#b91c1c' : '#065f46', fontWeight: 600 }}>
              {record.fedKg > 0
                ? t('lots.transferPlan.fastingFed', { date: formatDay(record.day), kg: fmt(record.fedKg, 3) })
                : t('lots.transferPlan.fastingKept', { date: formatDay(record.day) })}
            </span>
          ))}
        </div>
      ) : null}

      {editing ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'grid', gap: 2 }}>
            <span style={label}>{t('lots.transferPlan.date')}</span>
            <input
              type="date"
              data-testid="lot-transfer-plan-date"
              value={date}
              min={today}
              onChange={(event) => setDate(event.target.value)}
              style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8 }}
            />
          </label>
          <label style={{ display: 'grid', gap: 2 }}>
            <span style={label}>{t('lots.transferPlan.band')}</span>
            <select
              data-testid="lot-transfer-plan-band"
              value={bandChoice}
              onChange={(event) => setBandChoice(event.target.value)}
              style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8 }}
            >
              <option value="">{proposedIndex >= 0 ? t('lots.transferPlan.proposedBand', { band: proposedIndex + 1 }) : t('lots.transferPlan.chooseBand')}</option>
              {bands.map((band, index) => (
                <option key={index} value={String(index)}>
                  {t('lots.transferPlan.bandOption', {
                    band: index + 1,
                    weight: band.maxWeightG === null ? t('lots.transferPlan.openBand') : `≤ ${fmt(band.maxWeightG)} g`,
                    fast: band.fastDays,
                    days: band.reducedDays,
                    delta: fmt(band.deltaC)
                  })}
                </option>
              ))}
            </select>
          </label>
          <GradientButton size="xs" variant="success" disabled={saving} onClick={() => void save()} data-testid="lot-transfer-plan-save">
            {t('common.save')}
          </GradientButton>
          <GradientButton size="xs" variant="secondary" disabled={saving} onClick={() => setEditing(false)}>
            {t('common.cancel')}
          </GradientButton>
        </div>
      ) : canEdit ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {bands.length > 0 ? (
            <GradientButton size="xs" variant="info" onClick={startEditing} data-testid="lot-transfer-plan-edit">
              {t(planned ? 'lots.transferPlan.change' : 'lots.transferPlan.schedule')}
            </GradientButton>
          ) : null}
          {planned ? (
            <GradientButton size="xs" variant="secondary" disabled={saving} onClick={() => void save(true)} data-testid="lot-transfer-plan-clear">
              {t('lots.transferPlan.clear')}
            </GradientButton>
          ) : null}
        </div>
      ) : null}
      {planned && !editing ? <span style={{ fontSize: 12, color: '#6b7280' }}>{t('lots.transferPlan.help')}</span> : null}
      {error ? <div role="alert" style={{ color: '#b91c1c', fontWeight: 700 }}>{error}</div> : null}
    </div>
  )
}
